import { useRef } from 'react';

import { format, parseJSON } from 'date-fns';

import { TableCell, Typography } from '@mui/material';
import { tss } from 'tss-react/mui';

import { useDraggable } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities/dist/css';

import { nonFalsey } from '@/utils/nonFalsey';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { FileIcon } from '../../../FileIcon';

import { SignerDescription } from './filename-cell/SignerDescription';
import { ChangeTextPopover } from './filename-cell/ChangeTextPopover';
import { FileLink } from './filename-cell/FileLink';
import type { OwnerCandidate } from './FileOwnerSelector';

const FILENAME_CELL_FRAGMENT = graphql(`
  fragment filenameCellFields on File {
    ...fileFields
  }
`);

type Props = {
  file: DocumentType<typeof FILENAME_CELL_FRAGMENT>;
  canEdit?: boolean;
  isFirstRestricted?: boolean;
  renameFileOpen?: boolean;
  closeRenameFile?: () => void;
} & (
  | {
      ownerCandidates?: OwnerCandidate[];
      isSearchResult?: unknown;
    }
  | {
      ownerCandidates?: unknown;
      isSearchResult: true;
    }
);

export const useStyles = tss
  .withParams<{ transform: Transform | null }>()
  .create(({ theme, transform }) => {
    return {
      icon: {
        marginRight: theme.spacing(1),
        flexShrink: 0,
      },
      signedBy: {
        color: theme.palette.grey[600],
      },
      review: {
        color: theme.palette.grey[600],
      },
      restricted: {
        color: theme.palette.error.main,
      },
      reviewComingSoon: {
        '&:first-child': {
          borderLeftColor: theme.palette.warning.main,
        },

        color: theme.palette.warning.dark,
      },
      reviewHappened: {
        '&:first-child': {
          borderLeftColor: theme.palette.error.main,
        },

        color: theme.palette.error.main,
      },
      reviewCell: {
        borderLeftColor: theme.palette.success.light,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
      },
      pinned: {
        borderLeftColor: theme.palette.primary.main,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
      },
      filename: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      },
      nameCell: {
        display: 'inline-flex',
        alignItems: 'center',
        width: '100%',
        wordBreak: 'break-all',
        minWidth: 1,
        boxSizing: 'border-box',
        paddingLeft: theme.spacing(2),
      },
      isDragging: {
        opacity: 0.3,
      },
      transform: transform
        ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          }
        : {},
    };
  });

export const FilenameCell = ({
  file,
  ownerCandidates,
  canEdit = false,
  isFirstRestricted = false,
  renameFileOpen = false,
  closeRenameFile,
  isSearchResult = false,
}: Props) => {
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: file._id ?? '',
    data: { file, ownerCandidates },
    disabled: !canEdit || Boolean(isSearchResult) || renameFileOpen,
  });

  const anchorRef = useRef<HTMLDivElement>(null);
  const { classes, cx } = useStyles({ transform });

  const { _id: fileId, filename } = file;

  const draggableAttributes = {
    role: 'cell',
    tabIndex: 0,
    'aria-roledescription': 'draggable',
    'aria-describedby': `draggable file ${filename}`,
  };

  if (!(fileId && filename)) {
    return <TableCell />;
  }

  const reviewWarningClasses = {
    [classes.reviewComingSoon]:
      file.workflow?.reviewDateWarning === 'COMING_SOON',
    [classes.reviewHappened]: file.workflow?.reviewDateWarning === 'HAPPENED',
  };

  const reviewDate = file.workflow?.reviewDate;
  const signatures = file.signatures?.filter(nonFalsey) || [];

  return (
    <TableCell
      padding="none"
      className={cx({
        [classes.pinned]: file.pinned,
        ...reviewWarningClasses,
        [classes.reviewCell]: Boolean(reviewDate),
        [classes.transform]: Boolean(transform),
      })}
      {...draggableAttributes}
      {...listeners}
    >
      <div
        className={cx(classes.nameCell, {
          [classes.isDragging]: isDragging,
        })}
        ref={setNodeRef}
      >
        <FileIcon className={classes.icon} file={file} />{' '}
        <div className={classes.filename} ref={anchorRef}>
          <FileLink
            file={file}
            showParentLink={Boolean(isSearchResult)}
            isDragging={isDragging}
          />
          {!isSearchResult && reviewDate ? (
            <>
              <Typography
                className={cx(classes.review, reviewWarningClasses)}
                variant="caption"
              >
                Review due on {format(parseJSON(reviewDate), 'dd MMM yyyy')}
              </Typography>
            </>
          ) : null}
          {!isSearchResult && isFirstRestricted ? (
            <>
              <Typography className={classes.restricted} variant="caption">
                Restricted visibility
              </Typography>
            </>
          ) : null}
          {!isSearchResult && signatures.length > 0 && (
            <>
              <Typography className={classes.signedBy} variant="caption">
                Marked as signed by <SignerDescription file={file} />
              </Typography>
            </>
          )}
          <ChangeTextPopover
            file={file}
            anchorEl={anchorRef.current}
            open={renameFileOpen}
            onClose={() => {
              if (closeRenameFile) {
                closeRenameFile();
              }
            }}
          />
        </div>
      </div>
    </TableCell>
  );
};
